import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ThemeProvider } from '@/components/theme-provider';
import { routeTree } from './routeTree.gen';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

const router = createRouter({ routeTree, context: { queryClient } });

declare module '@tanstack/react-router' {
  interface Register { router: typeof router; }
}

// Inline styles (not theme classes) so the fallback renders even if a
// stylesheet/theme failed to load as part of the crash.
function CrashFallback() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#070b12',
        color: '#e6ecf5',
        fontFamily: 'Inter, system-ui, sans-serif',
        textAlign: 'center',
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 400 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 8px' }}>Something went wrong</h1>
        <p style={{ color: '#8a96aa', margin: '0 0 16px', lineHeight: 1.5 }}>
          The page hit an unexpected error. Your data is safe — reloading usually fixes it.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            background: '#22d3ee',
            color: '#06121a',
            border: 0,
            borderRadius: 8,
            padding: '10px 18px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </div>
    </div>
  );
}

// Vendor-free top-level error boundary — catches any React render crash and
// shows the fallback instead of a white screen. (Replaced @sentry/react.)
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  override state: { hasError: boolean } = { hasError: false };
  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }
  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('App crash:', error, info.componentStack);
  }
  override render(): ReactNode {
    return this.state.hasError ? <CrashFallback /> : this.props.children;
  }
}

export function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
